import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const humanGeographyUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Population and Migration Patterns and Processes",
  examWeight: "12–17%",
  focus: "Understanding population distribution, composition, dynamics, and migration patterns. How populations change and move across space.",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Population Distribution",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Physical Factors Influencing Distribution</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Humans generally avoid settling in environments that are too dry, too wet, too cold, or too high. We cluster in the <strong>Ecumene</strong> (the portion of Earth's surface occupied by permanent human settlement).
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🌡️ Climate</h4>
                <p className="text-sm text-slate-700 mt-1">People prefer low-lying areas with temperate climates and adequate rainfall (Mid-latitudes).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">⛰️ Landforms</h4>
                <p className="text-sm text-slate-700 mt-1">People settle near river valleys and deltas (fertile soil) and coasts (transportation/trade).</p>
              </div>
            </div>

            <h4 className="text-lg font-bold text-slate-800 mb-3 mt-6">Major Population Clusters</h4>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-800">🇨🇳 East Asia</h5>
                <p className="text-sm text-slate-700 mt-1">China, Japan, Koreas – approx. 1/4 of world population</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">🇮🇳 South Asia</h5>
                <p className="text-sm text-slate-700 mt-1">India, Pakistan, Bangladesh – approx. 1/4 of world population</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">🇪🇺 Europe</h5>
                <p className="text-sm text-slate-700 mt-1">High concentration in Western Europe; reliant on industry/services</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-bold text-green-800">🏝️ Southeast Asia</h5>
                <p className="text-sm text-slate-700 mt-1">Indonesia, Philippines – clustered on islands</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Human Factors Influencing Distribution</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">💼 Economic</h4>
                <p className="text-sm text-slate-700 mt-1">Jobs, industrial areas, trade hubs attract people (Urbanization)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🕌 Cultural</h4>
                <p className="text-sm text-slate-700 mt-1">Religious significance or cultural isolation</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏛️ Political</h4>
                <p className="text-sm text-slate-700 mt-1">Capital cities attract population (e.g., Brasilia)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Types of Population Density</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To analyze <em>pressure</em> on the land, geographers use three distinct metrics:
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">📊 Arithmetic Density</h4>
                <p className="text-sm text-slate-700 mt-1">Total number of people ÷ total land area</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Use:</strong> Gives a broad idea of "crowdedness"</p>
                <p className="text-xs text-red-700 mt-1"><strong>Limit:</strong> Doesn't tell if people can actually survive on that land</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🌾 Physiological Density</h4>
                <p className="text-sm text-slate-700 mt-1">Number of people per unit of <strong>arable land</strong> (land suitable for agriculture)</p>
                <p className="text-xs text-green-700 mt-2"><strong>Use:</strong> Reveals pressure on land to produce food</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Example:</strong> Egypt has high physiological density (95% of people on 5% of land along Nile)</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">👨‍🌾 Agricultural Density</h4>
                <p className="text-sm text-slate-700 mt-1">Number of <strong>farmers</strong> per unit of arable land</p>
                <p className="text-xs text-orange-700 mt-2"><strong>Use:</strong> Indicates level of development</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Trend:</strong> MDCs have LOW (technology replaces labor); LDCs have HIGH (subsistence farming)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Consequences of Population Distribution",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Carrying Capacity</h3>
            <KeyTerm 
              term="Carrying Capacity" 
              definition="The maximum population size that the environment can sustain indefinitely, given available food, habitat, water, and necessities."
            />
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">⚠️ Overpopulation</h4>
                <p className="text-sm text-slate-700 mt-1">Occurs when population exceeds carrying capacity.</p>
                <p className="text-xs text-red-700 mt-2"><strong>Impact:</strong> Environmental degradation, resource depletion, water scarcity</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">✅ Sustainable</h4>
                <p className="text-sm text-slate-700 mt-1">Population within carrying capacity.</p>
                <p className="text-xs text-green-700 mt-2"><strong>Result:</strong> Resources available for future generations</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Political, Economic, and Social Consequences</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏛️ Political</h4>
                <p className="text-sm text-slate-700 mt-1">Distribution determines political influence. Census data in US determines Electoral College votes and House representatives per state (<strong>Redistricting</strong>).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">💰 Economic</h4>
                <p className="text-sm text-slate-700 mt-1">Uneven distribution leads to uneven development. Urban areas concentrate wealth and services; rural areas may lack hospitals/infrastructure.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">👥 Social</h4>
                <p className="text-sm text-slate-700 mt-1">High density facilitates disease spread but also allows cultural cohesiveness.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Population Composition",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Age-Sex Structures (Population Pyramids)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A bar graph displaying the percentage of a place's population for each age and gender.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <p className="text-sm text-slate-700"><strong>X-axis:</strong> Gender (Males left, Females right)</p>
              <p className="text-sm text-slate-700 mt-1"><strong>Y-axis:</strong> Age cohorts (usually 5-year groups)</p>
            </div>

            <h4 className="text-lg font-bold text-slate-800 mb-3">Analyzing Pyramids</h4>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-800">📈 Expanding (Stage 2/3)</h5>
                <p className="text-sm text-slate-700 mt-1">Wide base, narrow top. High birth rates and growing population.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Nigeria, Angola</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">📊 Stationary/Stable (Stage 4)</h5>
                <p className="text-sm text-slate-700 mt-1">Rectangular/Column shape. Birth rates equate to death rates.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> United States, France</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">📉 Constrictive/Contracting (Stage 5)</h5>
                <p className="text-sm text-slate-700 mt-1">Narrow base, wider middle/top. Birth rates lower than replacement level.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Japan, Germany</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Metrics in Composition</h3>
            <div className="space-y-3">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">👶👴 Dependency Ratio</h4>
                <p className="text-sm text-slate-700 mt-1">Number of people too young (0–14) or too old (65+) to work, compared to productive-age people.</p>
                <p className="text-xs text-orange-700 mt-2"><strong>Impact:</strong> High ratios strain economy (taxes, healthcare, schools)</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-800">👨👩 Sex Ratio</h4>
                <p className="text-sm text-slate-700 mt-1">Number of males per 100 females.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Natural pattern:</strong> 105 males born for every 100 females</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Distortions:</strong> War (fewer men), Migration (guest workers), Selective abortion (China/India)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Population Dynamics",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Factors Determining Growth/Decline</h3>
            <div className="space-y-3">
              <KeyTerm term="Crude Birth Rate (CBR)" definition="Live births per 1,000 people/year." />
              <KeyTerm term="Crude Death Rate (CDR)" definition="Deaths per 1,000 people/year." />
              <KeyTerm term="Rate of Natural Increase (RNI/NIR)" definition="(CBR - CDR) / 10. Note: This excludes migration." />
              <KeyTerm term="Total Fertility Rate (TFR)" definition="Average number of children a woman will have in her childbearing years (15–49). Replacement rate is 2.1." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Doubling Time (Rule of 70)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-800">⏱️ Formula</h4>
              <p className="text-sm text-slate-700 mt-2">70 ÷ RNI = Years to Double</p>
              <p className="text-sm text-slate-700 mt-3"><strong>Example:</strong> If a country has an RNI of 2%, it will double in 35 years (70/2)</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: The Demographic Transition Model (DTM)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Five Stages</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A model explaining the transition from high birth and death rates to low birth and death rates as a country develops.
            </p>

            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Stage 1: High Stationary</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Rates:</strong> High Birth, High Death</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Growth:</strong> Low (RNI near 0)</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Context:</strong> Pre-industrial, subsistence farming, pestilence, famine</p>
                <p className="text-xs text-red-700 mt-1"><strong>Note:</strong> No distinct countries in Stage 1 today</p>
              </div>

              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Stage 2: Early Expanding</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Rates:</strong> High Birth, Rapidly Declining Death</p>
                <p className="text-sm text-red-700 mt-1 font-semibold">Growth: EXPLOSIVE</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Causes:</strong> Industrial Revolution (MDCs) or Medical Revolution (LDCs)</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Examples:</strong> Sub-Saharan Africa (Mali, South Sudan)</p>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Stage 3: Late Expanding</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Rates:</strong> Declining Birth, Low Death</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Growth:</strong> Moderate</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Causes:</strong> Urbanization (kids become liabilities), female education</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Examples:</strong> Mexico, India, Brazil</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Stage 4: Low Stationary</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Rates:</strong> Low Birth, Low Death</p>
                <p className="text-sm text-blue-700 mt-1 font-semibold">Growth: Zero Population Growth (ZPG)</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Causes:</strong> Women in workforce, birth control, high cost of living</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Examples:</strong> USA, China, Australia</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Stage 5: Declining</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Rates:</strong> Birth rate drops BELOW Death rate</p>
                <p className="text-sm text-purple-700 mt-1 font-semibold">Growth: NEGATIVE</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Japan, Germany, Russia</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Epidemiological Transition Model (ETM)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Correlates with the DTM, focusing on causes of death.
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h5 className="font-semibold text-slate-800">Pestilence and Famine</h5>
                  <p className="text-sm text-slate-600 mt-1">Black Plague</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h5 className="font-semibold text-slate-800">Receding Pandemics</h5>
                  <p className="text-sm text-slate-600 mt-1">Cholera</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h5 className="font-semibold text-slate-800">Degenerative/Man-made Diseases</h5>
                  <p className="text-sm text-slate-600 mt-1">Cancer, Heart Disease</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h5 className="font-semibold text-slate-800">Delayed Degenerative Diseases</h5>
                  <p className="text-sm text-slate-600 mt-1">Medicine extends life despite illness</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h5 className="font-semibold text-slate-800">Reemergence of Infectious Diseases</h5>
                  <p className="text-sm text-slate-600 mt-1">Evolution of microbes, poverty, travel</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Malthusian Theory",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Thomas Malthus (1798)</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4">
              <h4 className="font-bold text-red-800">⚠️ The Theory</h4>
              <p className="text-sm text-slate-700 mt-2">Food production grows <strong>arithmetically</strong> (1, 2, 3, 4), while population grows <strong>geometrically</strong> (1, 2, 4, 8).</p>
              <p className="text-sm text-red-700 mt-3 font-semibold">Conclusion: Population will outpace food supply, leading to "Malthusian Catastrophe" (war, famine, disease)</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Neo-Malthusians</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">Modern supporters of Malthus. Argue the gap is not just food, but broadly about resources (energy, water, clean air). Point to rapid growth in LDCs as threat to global stability.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Critics (Boserup Theory/Cornucopians)</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-800">✅ Boserup's Counter-Argument</h4>
              <p className="text-sm text-slate-700 mt-2">Population growth drives technological innovation. "Necessity is the mother of invention."</p>
              <p className="text-sm text-green-700 mt-3"><strong>Evidence:</strong> The Green Revolution (GMOs, fertilizers) allowed food production to outpace population growth, proving Malthus wrong so far.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: Population Policies",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Pronatalist Policies</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-800">👶 Goal: Increase Birth Rates</h4>
              <p className="text-sm text-slate-700 mt-2">Usually found in Stage 4/5 countries worried about shrinking workforce.</p>
              <p className="text-sm text-slate-700 mt-3"><strong>Methods:</strong> Tax breaks for children, paid maternity/paternity leave, subsidized childcare</p>
              <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> France, Sweden, Singapore ("National Night"), Japan</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Antinatalist Policies</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-bold text-red-800">🚫 Goal: Decrease Birth Rates</h4>
              <p className="text-sm text-slate-700 mt-2">Found in Stage 2/3 countries worried about overpopulation.</p>
              <p className="text-sm text-slate-700 mt-3"><strong>Methods:</strong> Free contraceptives, tax penalties for large families, education programs</p>
              <div className="space-y-2 mt-3">
                <p className="text-xs text-slate-600"><strong>China:</strong> One-Child Policy (very strict, effective, resulted in gender imbalance and aging population)</p>
                <p className="text-xs text-slate-600"><strong>India:</strong> Sterilization camps in 1970s; now focuses on education/family planning</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-8",
      title: "Topic 2.8: Women and Demographic Change",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The "Silver Bullet" for Population Reduction</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              There is a strong negative correlation between women's rights/education and fertility rates.
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">📚 Education</h4>
                <p className="text-sm text-slate-700 mt-1">When women attend school longer, they marry later and start families later, reducing reproductive years.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">💼 Employment</h4>
                <p className="text-sm text-slate-700 mt-1">Women in workforce have higher opportunity costs for having children.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">⚕️ Healthcare</h4>
                <p className="text-sm text-slate-700 mt-1">Reduced Infant Mortality Rates (IMR) mean women don't need as many children to ensure some survive.</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-800">🛡️ Contraception</h4>
                <p className="text-sm text-slate-700 mt-1">Knowledge of and access to family planning allows control over birth spacing.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-9",
      title: "Topic 2.9: Aging Populations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Causes</h3>
            <p className="text-slate-700 leading-relaxed">
              High life expectancy (healthcare/diet) + Low fertility rates (lifestyle changes)
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Consequences</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">💰 Economic</h4>
                <p className="text-sm text-slate-700 mt-1">Shrinking workforce leads to labor shortages (may require automation or immigration). High "Old-Age Dependency Ratio" strains pension systems (Social Security) and healthcare.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🏛️ Political</h4>
                <p className="text-sm text-slate-700 mt-1">The "Gray Vote"—policies shift to favor the elderly (e.g., funding for Medicare over education).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">👥 Social</h4>
                <p className="text-sm text-slate-700 mt-1">Need for retirement communities and nursing homes (Silver Economy).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-10",
      title: "Topic 2.10: Causes of Migration",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Push and Pull Factors</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">📤 Push Factors</h4>
                <p className="text-sm text-slate-700 mt-2">Negative circumstances that force people to leave:</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li><strong>Economic:</strong> Unemployment, poverty</li>
                  <li><strong>Social:</strong> Religious persecution, discrimination</li>
                  <li><strong>Political:</strong> War, authoritarian regimes</li>
                  <li><strong>Environmental:</strong> Drought, flood, disasters</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">📥 Pull Factors</h4>
                <p className="text-sm text-slate-700 mt-2">Positive circumstances that attract people:</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li><strong>Economic:</strong> Jobs, higher wages</li>
                  <li><strong>Social:</strong> Freedom, family ties (Kinship links)</li>
                  <li><strong>Political:</strong> Democracy, peace</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Intervening Obstacles and Opportunities</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🚧 Obstacle</h4>
                <p className="text-sm text-slate-700 mt-1">A barrier preventing migration (mountains, deserts, border walls, visa requirements).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">✨ Opportunity</h4>
                <p className="text-sm text-slate-700 mt-1">A positive circumstance along the migration route that stops the migrant from reaching the original destination (e.g., finding a good job halfway there).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-11",
      title: "Topic 2.11: Forced and Voluntary Migration",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Voluntary Migration</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The migrant chooses to move, usually for economic reasons.
            </p>
            <div className="space-y-3">
              <KeyTerm term="Transnational" definition="Moving across international borders." />
              <KeyTerm term="Internal" definition="Moving within a country (e.g., Rust Belt to Sun Belt in US)." />
              <KeyTerm term="Chain Migration" definition="Moving to a location because relatives or same nationality are already there (creates ethnic enclaves)." />
              <KeyTerm term="Step Migration" definition="Migration occurring in stages (Farm → Village → Town → City)." />
              <KeyTerm term="Rural-to-Urban" definition="The most common internal migration pattern (Urbanization)." />
              <KeyTerm term="Transhumance" definition="Seasonal movement of livestock herders (winter in valleys, summer in hills)." />
              <KeyTerm term="Guest Workers" definition="Temporary migrants allowed into a country for labor (e.g., Turks in Germany)." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Forced Migration</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The migrant has no choice but to move due to threats to life.
            </p>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🚨 Refugee</h4>
                <p className="text-sm text-slate-700 mt-1">Someone who crosses an international border to flee war, persecution, or disaster. Protected by UN laws.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">⚠️ Internally Displaced Person (IDP)</h4>
                <p className="text-sm text-slate-700 mt-1">Someone forced to flee but <strong>remains within</strong> their country's borders. More vulnerable legally.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-800">📋 Asylum Seeker</h4>
                <p className="text-sm text-slate-700 mt-1">Someone who has migrated to another country hoping to be recognized as a refugee.</p>
              </div>
            </div>
            <ImportantNote>
              <strong>Historical Example:</strong> The Atlantic Slave Trade
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-12",
      title: "Topic 2.12: Effects of Migration",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">On Countries of Origin (The place left behind)</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🧠 Brain Drain</h4>
                <p className="text-sm text-slate-700 mt-1">The loss of highly skilled professionals (doctors, engineers) to other countries.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">💵 Remittances</h4>
                <p className="text-sm text-slate-700 mt-1">Money sent back home by migrants. A massive source of income for many LDCs.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">📉 Relief from Overcrowding</h4>
                <p className="text-sm text-slate-700 mt-1">Reduces unemployment rates in the source country.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">On Countries of Destination (The receiving place)</h3>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🎭 Cultural</h4>
                <p className="text-sm text-slate-700 mt-1">Introduction of new languages, religions, and foods (Cultural Diffusion). Can lead to <strong>Syncretism</strong> (blending) or conflict/xenophobia.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">💼 Economic</h4>
                <p className="text-sm text-slate-700 mt-1">Migrants often fill "3D jobs" (Dirty, Dangerous, Difficult) that locals refuse, or provide highly skilled labor.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">📚 Historical Impact</h4>
                <p className="text-sm text-slate-700 mt-1">Examples: Great Migration (African Americans moving North in US) or colonization.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Important Models & Theories</h3>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">📍 Ravenstein's Laws of Migration</h4>
                <ol className="text-xs text-slate-600 space-y-1 list-decimal pl-4 mt-2">
                  <li>Most migrants move only a short distance</li>
                  <li>Migration proceeds step-by-step</li>
                  <li>Long-distance migrants go to big cities</li>
                  <li>Every migration flow produces a counter-flow</li>
                  <li>Urban dwellers are less migratory than rural people</li>
                  <li>Families are less likely to make international moves than young adults</li>
                </ol>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🔄 Zelinsky's Model of Migration Transition</h4>
                <p className="text-sm text-slate-700 mt-2">Links migration patterns to the DTM.</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li><strong>Stage 2:</strong> High international emigration (people leaving crowded LDCs)</li>
                  <li><strong>Stage 3/4:</strong> High internal migration and reception of international immigrants</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default humanGeographyUnit2Notes;